import React, { useState, useEffect } from "react";
import { db } from "../Firebase/config";
import { collection, getDocs, orderBy, limit, startAfter, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Box } from '@mui/material';
import { Container, Table, Button, Alert, Form, Row, Col, Badge } from "react-bootstrap";
import * as XLSX from 'xlsx';

const AllCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  // Filter and search state
  const [routeFilter, setRouteFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [routes, setRoutes] = useState([]);

  const fetchCustomers = async (isFirst = true) => {
    try {
      if (isFirst) setLoading(true);
      else setLoadingMore(true);

      const customersRef = collection(db, "customers");
      let q = query(customersRef, orderBy("name"), limit(30));

      if (!isFirst && lastVisible) {
        q = query(customersRef, orderBy("name"), startAfter(lastVisible), limit(30));
      }

      const querySnapshot = await getDocs(q);
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);

      if (querySnapshot.docs.length < 30) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      const customersData = querySnapshot.docs.map(doc => ({
        firebaseId: doc.id,
        ...doc.data()
      }));

      if (isFirst) {
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } else {
        setCustomers(prev => [...prev, ...customersData]);
        setFilteredCustomers(prev => [...prev, ...customersData]);
      }

      // Update unique routes
      const allLoaded = isFirst ? customersData : [...customers, ...customersData];
      const uniqueRoutes = [...new Set(allLoaded.map(c => c.route))].filter(r => r);
      setRoutes(uniqueRoutes);

    } catch (err) {
      console.error("Error fetching customers: ", err);
      setError("Failed to load customers. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchCustomers(true);
  }, []);

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchCustomers(false);
    }
  };

  // Apply filters and search
  useEffect(() => {
    let result = [...customers];

    // Apply route filter
    if (routeFilter) {
      result = result.filter(customer => customer.route === routeFilter);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(customer =>
        (customer.name && customer.name.toLowerCase().includes(term)) ||
        (customer.phone && customer.phone.includes(term)) ||
        (customer.ownerName && customer.ownerName.toLowerCase().includes(term)) ||
        (customer.ownerPhone && customer.ownerPhone.includes(term))
      );
    }

    setFilteredCustomers(result);
  }, [customers, routeFilter, searchTerm]);

  const currentItems = filteredCustomers;

  const handleViewDetails = (firebaseId) => {
    navigate(`/customer/${firebaseId}`);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredCustomers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    XLSX.writeFile(workbook, "customers.xlsx");
  };

  // Calculate totals
  const totalCustomers = filteredCustomers.length;
  const totalGasOnHand = filteredCustomers.reduce((sum, customer) => sum + (customer.currentGasOnHand || 0), 0);
  const totalBalance = filteredCustomers.reduce(
    (sum, customer) => sum + Number(customer.currentBalance ?? 0),
    0
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <img
          src="https://cdn.pixabay.com/animation/2022/07/29/03/42/03-42-11-849_512.gif"
          alt="Loading..."
          style={{ width: '150px', height: '150px' }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>All Customers ({filteredCustomers.length})</h2>
          <Badge bg="info">Summary based on loaded items</Badge>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/new-connection")}
        >
          Add New Customer
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="mb-4 p-3 bg-light rounded shadow-sm">
        <Row>
          <Col md={4}>
            <Form.Group controlId="routeFilter">
              <Form.Label>Filter by Route</Form.Label>
              <Form.Control
                as="select"
                value={routeFilter}
                onChange={(e) => setRouteFilter(e.target.value)}
              >
                <option value="">All Routes</option>
                {routes.map((route, index) => (
                  <option key={index} value={route}>{route}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="search">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name, phone, owner name, or owner phone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button variant="success" onClick={exportToExcel}>
              Export
            </Button>
          </Col>
        </Row>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <div className="p-3 bg-info rounded shadow-sm">
            <h5>Total Customers</h5>
            <h3><Badge bg="" text="dark">{totalCustomers}</Badge></h3>
          </div>
        </Col>
        <Col md={4}>
          <div className="p-3 bg-warning rounded shadow-sm">
            <h5>Total Gas On Hand</h5>
            <h3><Badge bg="" text="dark">{totalGasOnHand.toLocaleString('en-IN')} kg</Badge></h3>
          </div>
        </Col>
        <Col md={4}>
          <div className="p-3 bg-danger rounded shadow-sm">
            <h5 className="text-light">Total Balance</h5>
            <h3>
              <Badge bg="">
                ₹{totalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Badge>
            </h3>
          </div>
        </Col>
      </Row>

      {/* Customers Table */}
      <div className="table-responsive mb-4">
        <Table striped bordered hover className="shadow-sm">
          <thead className="bg-primary text-white">
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Organization</th>
              <th>Phone</th>
              <th>Route</th>
              <th>Current Balance</th>
              <th>Gas On Hand</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((customer) => (
                <tr key={customer.firebaseId}>
                  <td>{customer.id || '-'}</td>
                  <td>{customer.name}</td>
                  <td>{customer.organization || '-'}</td>
                  <td>{customer.phone}</td>
                  <td>{customer.route || '-'}</td>
                  <td>₹{customer.currentBalance?.toLocaleString('en-IN') || '0'}</td>
                  <td>{customer.currentGasOnHand?.toLocaleString('en-IN') || '0'} kg</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleViewDetails(customer.firebaseId)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center">No customers found</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="d-flex justify-content-center mt-4 mb-5">
          <Button
            variant="outline-primary"
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{ minWidth: '200px' }}
          >
            {loadingMore ? 'Loading more...' : 'Load More Customers'}
          </Button>
        </div>
      )}

      {/* Showing X items */}
      <div className="text-muted mt-2">
        Showing {filteredCustomers.length} loaded entries
        {routeFilter && ` (filtered by route: ${routeFilter})`}
        {searchTerm && ` (searched for: "${searchTerm}")`}
      </div>
    </Container>
  );
};

export default AllCustomers;